import { makeObservable } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { audioRecordingModule } from "@/modules/audio-recording";
import { combineSequentialTranscriptTurnGroups } from "@/modules/audio-recording/handoff-transcript-turns";
import { getMeetingHandoffPreviousAudioRecordingId } from "@/store/audio-recordings/app-events";
import { TranscriptionStatus } from "@/store/audio/recording";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  AudioRecordingTranscriptState,
  AudioRecordingTranscriptStatus,
  IAudioTranscriptionStore,
  TRANSCRIPTION_COMPROMISED_ERROR_MESSAGE,
} from "@/store/audio/transcription/audio-transcription.types";
import type { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import type { AudioRecordingModelData } from "@/store/audio-recordings/types";

export class AppStoreAudioTranscriptionStore
  extends AppSubStore
  implements IAudioTranscriptionStore
{
  private readonly localAudioRecordingTranscriptTurns: LocalAudioRecordingTranscriptTurnsRepository;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.localAudioRecordingTranscriptTurns =
      injectedDeps.store.dexie.repositories.localAudioRecordingTranscriptTurns;

    makeObservable<
      AppStoreAudioTranscriptionStore,
      "localAudioRecordingTranscriptTurns" | "getAudioRecordingModelDataForTranscript"
    >(this, {
      localAudioRecordingTranscriptTurns: false,
      getTranscriptState: false,
      getAudioRecordingModelDataForTranscript: false,
      clearTranscriptionStatus: false,
    });
  }

  async getTranscriptState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<AudioRecordingTranscriptState> {
    const transcriptionStatus = this.store.audio.recording.getTranscriptionStatus(audioRecordingId);

    if (transcriptionStatus === TranscriptionStatus.Compromised) {
      return {
        status: AudioRecordingTranscriptStatus.Compromised,
        content: "",
        transcriptArray: [],
        errorMessage: TRANSCRIPTION_COMPROMISED_ERROR_MESSAGE,
      };
    }

    const recordingModelData = await this.getAudioRecordingModelDataForTranscript({
      audioRecordingId,
    });
    const handoffPreviousAudioRecordingId = recordingModelData
      ? getMeetingHandoffPreviousAudioRecordingId({ modelData: recordingModelData })
      : null;
    const recordingIds = handoffPreviousAudioRecordingId
      ? [handoffPreviousAudioRecordingId, audioRecordingId]
      : [audioRecordingId];
    const transcriptTurns = combineSequentialTranscriptTurnGroups(
      await Promise.all(
        recordingIds.map(associatedAudioRecordingId =>
          this.localAudioRecordingTranscriptTurns.getAllByRecordingId({
            associatedAudioRecordingId,
          })
        )
      )
    );
    const { transcriptText, transcriptArray } =
      audioRecordingModule.buildTranscriptPayload(transcriptTurns);

    if (!transcriptText.trim()) {
      return {
        status: AudioRecordingTranscriptStatus.Missing,
        content: transcriptText,
        transcriptArray: [],
        errorMessage: "No transcript available",
      };
    }

    return {
      status: AudioRecordingTranscriptStatus.Ready,
      content: transcriptText,
      transcriptArray,
      errorMessage: null,
    };
  }

  clearTranscriptionStatus({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.store.audio.recording.clearTranscriptionStatus(audioRecordingId);
  }

  private async getAudioRecordingModelDataForTranscript({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<AudioRecordingModelData | null> {
    const localRow = await this.store.audioRecordings.localTable.get(audioRecordingId);
    if (localRow?.model_data) {
      return localRow.model_data;
    }

    const observableRecording = this.store.audioRecordings.get(audioRecordingId);
    if (observableRecording?.modelData) {
      return observableRecording.modelData;
    }

    return (await this.store.audioRecordings.getAsync(audioRecordingId))?.modelData ?? null;
  }
}
